import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import cx from "classnames";
import { useHistory, useParams } from "react-router-dom";
import { differenceInSeconds, format, isWithinInterval } from "date-fns";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Autoplay, Pagination } from "swiper";

import programGuideDemoImage from "assets/images/livetv/programguide-demo.png";
import "./LivePlayer.scss";
import { displayModal, displayUI, forceUI, hideUI } from "../storage/emitter";

import { fade } from "../storage/motionAnimations";
import { useUIShown } from "../storage/hooks";
import { View } from "../storage/routes";
import { dnsnetApi } from "../services/apis";
import { LinearProgress, Skeleton } from "@mui/material";
import { GreyButton } from "../Components/Buttons";
import VideoJS from "../Components/VideoJS";
import MenuBar from "../Components/Bars/MenuBar";

import LiveTVLogo from "assets/images/livetv.svg";
import appProperties from "../storage/app-properties";

SwiperCore.use([Autoplay, Pagination]);

const toHHMMSS = (secs) => {
  const sec_num = parseInt(secs, 10);
  const hours = Math.floor(sec_num / 3600);
  const minutes = Math.floor(sec_num / 60) % 60;
  const seconds = sec_num % 60;

  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? "0" + v : v))
    .filter((v, i) => v !== "00" || i > 0)
    .join(":");
};

const getPlayingAsset = (assets = []) => {
  return assets?.find((asset) => {
    const start = new Date(asset.start);
    const end = new Date(asset.end);

    return isWithinInterval(new Date(), { start, end });
  });
};

const getDuration = (asset) =>
  asset
    ? Math.abs(differenceInSeconds(new Date(asset.end), new Date(asset.start)))
    : 0;
const getPlayingProgress = (asset) =>
  asset ? differenceInSeconds(new Date(), new Date(asset.start)) : 0;

const ProgramInfo = ({ channel }) => {
  const [playingAsset, setPlayingAsset] = useState(
    getPlayingAsset(channel?.assets)
  );
  const [duration, setDuration] = useState(getDuration(playingAsset));
  const [currentTime, setCurrentTime] = useState(
    getPlayingProgress(playingAsset)
  );
  const [progress, setProgress] = useState((currentTime * 100) / duration);

  useEffect(() => {
    const updateAsset = () => {
      const playingAsset = getPlayingAsset(channel?.assets);
      const duration = getDuration(playingAsset);
      const currentTime = getPlayingProgress(playingAsset);
      setPlayingAsset(playingAsset);
      setDuration(duration);
      setCurrentTime(currentTime);
      setProgress((currentTime * 100) / duration);
    };

    updateAsset();
    const progressUpdater = setInterval(() => {
      updateAsset();
    }, 1e3);
    return () => {
      clearInterval(progressUpdater);
    };
  }, [channel]);
  return (
    <div className="channel-info">
      <div className="asset-info">
        <div className="channel-logo">
          <img alt={channel?.name} src={channel?.logo} />
        </div>
        <div className="asset-details">
          <div className="asset-title">
            {playingAsset?.formatTitle?.content || playingAsset?.title?.content}
          </div>
          <div className="asset-subtitle">
            {playingAsset?.subtitle?.content}
          </div>
        </div>
        <GreyButton
          color="secondary"
          variant="contained"
          className="button button--big"
          onClick={() => displayModal("demo")}
        >
          More Info
        </GreyButton>
      </div>
      <div className="progress-info">
        <div className="current-time">
          {playingAsset?.start && format(new Date(playingAsset.start), "HH:mm")}
        </div>
        <LinearProgress variant="determinate" value={progress} />
        <div className="duration">
          {playingAsset?.end && format(new Date(playingAsset.end), "HH:mm")}
        </div>
      </div>
    </div>
  );
};

const ChannelSlide = ({ channel, pos }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  const backgroundImage = useMemo(() => channel?.logo, [channel]);

  const onImageLoad = useCallback(() => {
    setImgLoaded(true);
  }, [backgroundImage]);

  return (
    <div className="slide-wrapper" data-slide-pos={pos}>
      <div className="slide-image">
        <AnimatePresence>
          {!imgLoaded && (
            <motion.div
              variants={fade}
              initial="visible"
              animate="visible"
              exit="exit"
              className="skelly-wrapper"
            >
              <Skeleton
                className="skelly"
                animation="wave"
                variant="rectangular"
              />
            </motion.div>
          )}
        </AnimatePresence>
        <img
          className="asset-image"
          src={backgroundImage}
          onLoad={onImageLoad}
          alt="Asset Cover"
        />
      </div>
      <div className="overlay" />
    </div>
  );
};

const ChannelsCarousel = ({ channels, onChannelFocus, onChannelClick }) => {
  const { channelId } = useParams();
  const initialSlide = useMemo(
    () => channels.findIndex((channel) => channel.channelId == channelId),
    [channelId]
  );
  const slides = useMemo(() => {
    return channels?.map((channel, pos) => {
      return (
        <SwiperSlide key={channel.channelId} className="slide channel-slide">
          <ChannelSlide channel={channel} pos={pos} />
        </SwiperSlide>
      );
    });
  }, [channels]);

  return channels?.length <= 0 ? null : (
    <Swiper
      slidesPerView={"auto"}
      centeredSlides={true}
      loop={true}
      pagination={false}
      navigation={false}
      className="channels-carousel"
      onSlideChange={onChannelFocus}
      onTap={onChannelClick}
      observer
      observeParents
      observeSlideChildren
      initialSlide={initialSlide}
    >
      {slides}
    </Swiper>
  );
};

const LivePlayer = () => {
  const isUIShown = useUIShown();
  // const isUIShown = true;
  const history = useHistory();
  const [channels, setChannels] = useState();
  const [channel, setChannel] = useState();
  const [focusedChannel, setFocusedChannel] = useState();
  const [isProgramGuide, setProgramGuide] = useState(false);
  const { channelId } = useParams();
  const player = useRef(null);

  const buttons = useMemo(() => {
    return [
      {
        id: "live-tv",
        text: "Live TV",
        onClick: () => {
          setProgramGuide(false);
          forceUI(false);
        },
        isActive: !isProgramGuide,
      },
      {
        id: "program-guide",
        text: "Program Guide",
        onClick: () => {
          setProgramGuide(true);
          forceUI(true);
        },
        isActive: isProgramGuide,
      },
    ];
  }, [isProgramGuide]);

  const videoJsOptions = useMemo(() => {
    if (!channel) return {};
    console.log("Channel:", channel);
    return {
      // lookup the options in the docs for more options
      autoplay: true,
      controls: false,
      responsive: true,
      // fluid: true,
      fill: true,
      // inactivityTimeout: 0,
      controlBar: {
        fullscreenToggle: false,
        volumePanel: false,
      },
      sources: [
        {
          src: channel.url,
          // type: "application/dash+xml",
        },
      ],
    };
  }, [channel]);

  const onEnded = useCallback(() => {
    displayUI();
    history.goBack();
  }, [channelId]);

  useEffect(() => {
    console.log("ChannelID changed:", channelId);
    if (channelId !== undefined) {
      dnsnetApi.getChannelEPG(channelId).then((ch) => {
        if (ch) {
          setChannel(ch);
        } else {
          history.goBack();
        }
      });
    } else {
      dnsnetApi.getFirstChannelEPG().then((ch) => {
        history.replace("/live/" + ch.channelId);
      });
    }
  }, [channelId]);

  useEffect(() => {
    if (!isUIShown) {
      setFocusedChannel(channel);
    }
  }, [isUIShown]);

  useEffect(() => {
    dnsnetApi.getEpg().then(({ subscribedPackage }) => {
      // use only unencrypted channels.
      const filteredChannels = subscribedPackage?.channels.filter(
        (channel) => !channel.crypt && channel.assets?.length > 0
      );
      setChannels(filteredChannels);
      console.log(filteredChannels);
    });

    forceUI(false);

    return () => {
      forceUI(false);
    };
  }, []);

  const onUserActive = useCallback((event) => {
    displayUI();
  }, []);
  const onUserInactive = useCallback((event) => {
    if (!player.current.paused) hideUI();
  }, []);
  const onChannelFocus = useCallback(
    (swiper) => {
      console.log("onSlideChange:", swiper);
      const focusedChannelPosition = swiper.realIndex;
      const focusedChannel = channels[focusedChannelPosition];
      setFocusedChannel(focusedChannel);
    },
    [channels]
  );

  const onChannelClick = useCallback(
    (swiper) => {
      console.log("onSlideTap:", swiper, swiper.realIndex, swiper.clickedIndex);
      if (swiper.clickedIndex !== undefined) {
        const focusedChannelPosition =
          swiper.clickedSlide?.dataset?.swiperSlideIndex - 0 || 0;
        const focusedChannel = channels[focusedChannelPosition];
        setFocusedChannel(focusedChannel);
        swiper.slideTo(swiper.clickedIndex);
        history.replace(`/live/${focusedChannel.channelId}`);
      }
    },
    [channels]
  );

  return (
    <View style={{ height: "100%" }}>
      <div id="live-player-view">
        {channel && (
          <>
            <div
              className={cx("ui-wrapper", {
                "ui--hidden": !isUIShown,
                "ui--shown": isUIShown,
              })}
            >
              <div className="ui-overlay" />
              {/*<div className="back-button" onClick={onEnded} />*/}
              {appProperties.ltd && (
                <div className="brand--logo">
                  <img
                    src={LiveTVLogo}
                    alt="Foxxum - Live TV Logo"
                  />
                </div>
              )}
              <MenuBar buttons={buttons} />
              {!isProgramGuide && (
                <>
                  <ProgramInfo channel={focusedChannel || channel} />
                  {channels && (
                    <ChannelsCarousel
                      channels={channels}
                      onChannelClick={onChannelClick}
                      onChannelFocus={onChannelFocus}
                    />
                  )}
                </>
              )}
              {isProgramGuide && (
                <div id="program-guide-view">
                  <div className="overlay" />
                  <img
                    className="demo-image"
                    src={programGuideDemoImage}
                    alt="Program Guide Demo Image"
                  />
                </div>
              )}
            </div>
            {/*<Shaka ref={player} src={channel.url} onEnded={onEnded} />*/}
            <VideoJS
              ref={player}
              options={videoJsOptions}
              onEnded={onEnded}
              // onUserActive={onUserActive}
              // onUserInactive={onUserInactive}
            />
          </>
        )}
      </div>
    </View>
  );
};

export default LivePlayer;
